import Brand from "./Brand";
import { telegram } from "./links";
import React, { useEffect, useState } from "react";
const isLocal = ["localhost", "127.0.0.1", "[::1]"].includes(
  window.location.hostname,
);
const BLOCKED_COUNTRY_CODES = new Set([
  // European Union
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // Other blocked regions
  "JP",
  "IN",
  "US",
  "CA",
]);

const REGION_CACHE_KEY = "animix_region_status_v2";
const REGION_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const LANGUAGE_TO_COUNTRY_FALLBACK = {
  be: "BY",
  bg: "BG",
  cs: "CZ",
  da: "DK",
  de: "DE",
  el: "GR",
  es: "ES",
  et: "EE",
  fi: "FI",
  fr: "FR",
  ga: "IE",
  hi: "IN",
  hr: "HR",
  hu: "HU",
  it: "IT",
  ja: "JP",
  kk: "KZ",
  lb: "LU",
  lt: "LT",
  lv: "LV",
  mt: "MT",
  mo: "MD",
  nl: "NL",
  pl: "PL",
  pt: "PT",
  ro: "RO",
  ru: "RU",
  sk: "SK",
  sl: "SI",
  sv: "SE",
  uk: "UA",
};

const normalizeCountryCode = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const formatCountryName = (countryCode) => {
  const code = normalizeCountryCode(countryCode);
  if (!code) return "страна не определена";

  try {
    if (
      typeof Intl !== "undefined" &&
      typeof Intl.DisplayNames === "function"
    ) {
      const names = new Intl.DisplayNames(["ru"], { type: "region" });
      return names.of(code) || code;
    }
  } catch {
    // Ignore Intl errors and fallback to raw code.
  }

  return code;
};

const extractCountryCodeFromLocale = (locale) => {
  const parts = String(locale || "")
    .trim()
    .replace(/_/g, "-")
    .split("-")
    .filter(Boolean);

  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (/^[A-Za-z]{2}$/.test(parts[i])) {
      return normalizeCountryCode(parts[i]);
    }
  }

  return null;
};

const resolveCountryCodeFromLanguage = () => {
  if (typeof navigator === "undefined") return null;

  const localeCandidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
    navigator.userLanguage,
    navigator.browserLanguage,
  ].filter(Boolean);

  for (const locale of localeCandidates) {
    const countryCode = extractCountryCodeFromLocale(locale);
    if (countryCode) return countryCode;
  }

  for (const locale of localeCandidates) {
    const language = String(locale).split(/[-_]/)[0].toLowerCase();
    const mappedCountry = LANGUAGE_TO_COUNTRY_FALLBACK[language];
    if (mappedCountry) return mappedCountry;
  }

  return null;
};

const loadCachedRegionStatus = () => {
  try {
    const raw = localStorage.getItem(REGION_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const { status, checkedAt, countryCode } = parsed;
    const isFresh =
      Number.isFinite(checkedAt) &&
      Date.now() - checkedAt < REGION_CACHE_TTL_MS;
    const isValidStatus = status === "allowed" || status === "blocked";
    const normalizedCountryCode = normalizeCountryCode(countryCode);

    return isFresh && isValidStatus
      ? { status, countryCode: normalizedCountryCode || null }
      : null;
  } catch {
    return null;
  }
};

const saveCachedRegionStatus = (status, countryCode = null) => {
  try {
    localStorage.setItem(
      REGION_CACHE_KEY,
      JSON.stringify({
        status,
        countryCode: normalizeCountryCode(countryCode) || null,
        checkedAt: Date.now(),
      }),
    );
  } catch {
    // Ignore storage errors (private mode/quota).
  }
};

const fetchJsonWithTimeout = async (url, signal, timeoutMs = 5000) => {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

  const onAbort = () => timeoutController.abort();
  signal.addEventListener("abort", onAbort);

  try {
    const response = await fetch(url, {
      signal: timeoutController.signal,
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } finally {
    clearTimeout(timeoutId);
    signal.removeEventListener("abort", onAbort);
  }
};

const resolveCountryCode = async (signal) => {
  const providers = [
    {
      url: "https://ipwho.is/",
      pickCode: (data) =>
        data && data.success !== false ? data.country_code : null,
    },
    {
      url: "https://ipapi.co/json/",
      pickCode: (data) => data?.country_code,
    },
    {
      url: "https://ipinfo.io/json",
      pickCode: (data) => data?.country,
    },
  ];

  for (const provider of providers) {
    try {
      const data = await fetchJsonWithTimeout(provider.url, signal);
      const code = normalizeCountryCode(provider.pickCode(data));
      if (code) return code;
    } catch {
      // Try next provider.
    }
  }

  return null;
};

export default function RegionGate({ children }) {
  const [regionStatus, setRegionStatus] = useState(
    isLocal ? "allowed" : "checking",
  );
  const [detectedCountryCode, setDetectedCountryCode] = useState(null);

  useEffect(() => {
    if (isLocal) return;
    const controller = new AbortController();
    let mounted = true;

    const applyStatus = (status, countryCode = null) => {
      if (!mounted) return;
      setRegionStatus(status);
      setDetectedCountryCode(normalizeCountryCode(countryCode) || null);
      saveCachedRegionStatus(status, countryCode);
    };

    const cachedStatus = loadCachedRegionStatus();
    if (cachedStatus) {
      setRegionStatus(cachedStatus.status);
      setDetectedCountryCode(cachedStatus.countryCode);
      return () => {
        mounted = false;
        controller.abort();
      };
    }

    const checkRegion = async () => {
      let code = await resolveCountryCode(controller.signal);
      if (!code) {
        code = resolveCountryCodeFromLanguage();
      }

      // Fail-open: if both geo and locale fallback are unavailable, do not block everyone.
      if (!code) {
        applyStatus("allowed", null);
        return;
      }

      applyStatus(
        BLOCKED_COUNTRY_CODES.has(code) ? "blocked" : "allowed",
        code,
      );
    };

    checkRegion().catch(() => {
      if (!controller.signal.aborted) {
        applyStatus("allowed", null);
      }
    });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  if (regionStatus === "checking")
    return (
      <div className="region-screen">
        <Brand />
        <p>Проверяем доступность в вашем регионе…</p>
      </div>
    );
  if (regionStatus === "blocked")
    return (
      <div className="region-screen">
        <Brand />
        <h1>Пока недоступно в вашем регионе</h1>
        <p>{formatCountryName(detectedCountryCode)}</p>
        <a
          className="button secondary"
          href={telegram.main}
          target="_blank"
          rel="noreferrer"
        >
          Новости в Telegram
        </a>
      </div>
    );
  return children;
}
